import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface AdminAuthContextType {
  isAuthenticated: boolean;
  login: (password: string, rememberMe: boolean) => boolean;
  logout: () => void;
  extendSession: () => void;
  sessionExpiresIn: number | null;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const ADMIN_PASSWORD = 'Portland1978?!';
const AUTH_KEY = 'admin_auth';
const ACTIVITY_KEY = 'admin_last_activity';
const SESSION_TIMEOUT = 2 * 60 * 60 * 1000;
const REMEMBER_DURATION = 30 * 24 * 60 * 60 * 1000;

interface AuthData {
  authenticated: boolean;
  rememberMe: boolean;
  expiresAt: number;
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpiresIn, setSessionExpiresIn] = useState<number | null>(null);
  const [rememberMe, setRememberMe] = useState(false);

  // ── Define logout FIRST so all effects can reference it safely ──
  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(ACTIVITY_KEY);
    setIsAuthenticated(false);
    setRememberMe(false);
    setSessionExpiresIn(null);
  }, []);

  const updateActivity = useCallback(() => {
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
  }, []);

  // Check stored auth on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(AUTH_KEY);
      if (!stored) return;

      const data: AuthData = JSON.parse(stored);
      const now = Date.now();

      if (!data.authenticated || data.expiresAt <= now) {
        logout();
        return;
      }

      if (!data.rememberMe) {
        const lastActivity = localStorage.getItem(ACTIVITY_KEY);
        const lastActivityTime = lastActivity ? parseInt(lastActivity, 10) : now;
        if (now - lastActivityTime > SESSION_TIMEOUT) {
          logout();
          return;
        }
        updateActivity();
      }

      setIsAuthenticated(true);
      setRememberMe(data.rememberMe);
    } catch {
      logout();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount only

  // Track user activity for session timeout
  useEffect(() => {
    if (!isAuthenticated || rememberMe) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, updateActivity));
    return () => events.forEach(event => window.removeEventListener(event, updateActivity));
  }, [isAuthenticated, rememberMe, updateActivity]);

  // Session countdown timer
  useEffect(() => {
    if (!isAuthenticated || rememberMe) {
      setSessionExpiresIn(null);
      return;
    }

    const interval = setInterval(() => {
      const stored = localStorage.getItem(AUTH_KEY);
      const lastActivity = localStorage.getItem(ACTIVITY_KEY);

      if (!stored || !lastActivity) {
        logout();
        return;
      }

      const lastActivityTime = parseInt(lastActivity, 10);
      const remaining = SESSION_TIMEOUT - (Date.now() - lastActivityTime);

      if (remaining <= 0) {
        logout();
      } else {
        setSessionExpiresIn(Math.floor(remaining / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, rememberMe, logout]);

  const login = useCallback((password: string, remember: boolean): boolean => {
    if (password !== ADMIN_PASSWORD) return false;

    const now = Date.now();
    const authData: AuthData = {
      authenticated: true,
      rememberMe: remember,
      expiresAt: now + (remember ? REMEMBER_DURATION : SESSION_TIMEOUT),
    };

    localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
    if (!remember) localStorage.setItem(ACTIVITY_KEY, now.toString());

    setIsAuthenticated(true);
    setRememberMe(remember);
    return true;
  }, []);

  const extendSession = useCallback(() => {
    if (!isAuthenticated) return;
    const stored = localStorage.getItem(AUTH_KEY);
    if (!stored) return;
    try {
      const data: AuthData = JSON.parse(stored);
      data.expiresAt = Date.now() + SESSION_TIMEOUT;
      localStorage.setItem(AUTH_KEY, JSON.stringify(data));
      localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
    } catch {
      // ignore
    }
  }, [isAuthenticated]);

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, login, logout, extendSession, sessionExpiresIn }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
